import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import {
  Search,
  CheckCircle,
  Star,
  EyeOff,
  Eye,
  MapPin,
  Phone,
  Globe,
  Filter,
  Clock,
  Building2,
  Shield,
  X,
  ExternalLink,
  ChevronDown,
  AlertCircle,
  Check,
  Briefcase,
  Pencil
} from 'lucide-react'

const formatTimeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  const weeks = Math.floor(days / 7)
  return `${weeks}w ago`
}

const StatusBadge = ({ status }) => {
  const statusConfig = {
    unclaimed: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Unclaimed' },
    pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending' },
    approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
    rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' }
  }

  const config = statusConfig[status] || statusConfig.unclaimed

  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  )
}

const ProviderRow = ({ provider, onToggleVerified, onToggleFeatured, onToggleActive, onEdit }) => {
  return (
    <div className="card p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-black truncate">{provider.name}</h3>
            <StatusBadge status={provider.claim_status || 'unclaimed'} />
          </div>
          <div className="text-sm text-rdc-taupe space-y-1">
            {provider.location && (
              <div className="flex items-center gap-1">
                <MapPin size={14} />
                <span>{provider.location}</span>
              </div>
            )}
            {provider.category && (
              <div className="flex items-center gap-1">
                <Building2 size={14} />
                <span className="capitalize">{provider.category}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {provider.verified && (
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Check size={16} className="text-green-600" />
            </div>
          )}
          {provider.featured && (
            <div className="w-8 h-8 rounded-full bg-rdc-orange/10 flex items-center justify-center">
              <Star size={16} className="text-rdc-orange fill-current" />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {provider.phone && (
          <a
            href={`tel:${provider.phone}`}
            className="flex items-center gap-1 text-xs text-rdc-primary hover:underline"
          >
            <Phone size={12} />
            <span>{provider.phone}</span>
          </a>
        )}
        {provider.website && (
          <a
            href={provider.website}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-rdc-primary hover:underline"
          >
            <Globe size={12} />
            <span>Website</span>
          </a>
        )}
      </div>

      <div className="flex items-center gap-2 pt-3 border-t border-rdc-stone/20">
        <button
          onClick={() => onEdit(provider)}
          className="btn-ghost text-sm flex items-center gap-1 text-rdc-primary hover:bg-rdc-blue/10"
        >
          <Pencil size={16} />
          Edit
        </button>

        <button
          onClick={() => onToggleVerified(provider.id, !provider.verified)}
          className={`btn-ghost text-sm flex items-center gap-1 ${
            provider.verified ? 'text-green-600 hover:bg-green-50' : 'text-rdc-taupe hover:bg-rdc-cream'
          }`}
        >
          <CheckCircle size={16} />
          {provider.verified ? 'Verified' : 'Verify'}
        </button>

        <button
          onClick={() => onToggleFeatured(provider.id, !provider.featured)}
          className={`btn-ghost text-sm flex items-center gap-1 ${
            provider.featured ? 'text-rdc-orange hover:bg-rdc-orange/10' : 'text-rdc-taupe hover:bg-rdc-cream'
          }`}
        >
          <Star size={16} />
          {provider.featured ? 'Featured' : 'Feature'}
        </button>

        <button
          onClick={() => onToggleActive(provider.id, !provider.is_active)}
          className={`btn-ghost text-sm flex items-center gap-1 ml-auto ${
            provider.is_active ? 'text-rdc-taupe hover:bg-rdc-cream' : 'text-red-600 hover:bg-red-50'
          }`}
        >
          {provider.is_active ? (
            <>
              <Eye size={16} />
              Active
            </>
          ) : (
            <>
              <EyeOff size={16} />
              Inactive
            </>
          )}
        </button>
      </div>
    </div>
  )
}

const ClaimRequestRow = ({ request, onApprove, onReject }) => {
  const [expanding, setExpanding] = useState(false)

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-black mb-1">
            {request.provider_id ? request.provider?.name : request.business_name}
          </h3>
          <div className="text-sm text-rdc-taupe space-y-0.5">
            <div>
              <strong>Requester:</strong> {request.requester_name}
            </div>
            <div className="flex items-center gap-1">
              <Clock size={12} />
              <span>{formatTimeAgo(request.created_at)}</span>
            </div>
          </div>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700`}>
          Pending
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-sm">
        <div className="flex items-center gap-2">
          <span className="text-xs text-rdc-taupe">Email:</span>
          <a href={`mailto:${request.requester_email}`} className="text-rdc-primary hover:underline truncate">
            {request.requester_email}
          </a>
        </div>
        {request.requester_phone && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-rdc-taupe">Phone:</span>
            <a href={`tel:${request.requester_phone}`} className="text-rdc-primary hover:underline">
              {request.requester_phone}
            </a>
          </div>
        )}
      </div>

      {request.business_proof && (
        <div className="mb-3">
          <button
            onClick={() => setExpanding(!expanding)}
            className="text-xs text-rdc-orange font-medium flex items-center gap-1"
          >
            {expanding ? 'Hide' : 'View'} Business Proof
            <ChevronDown size={14} className={`transition-transform ${expanding ? 'rotate-180' : ''}`} />
          </button>
          {expanding && (
            <div className="mt-2 p-3 bg-rdc-cream/50 rounded-lg text-sm text-rdc-charcoal max-h-40 overflow-y-auto">
              {request.business_proof}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 pt-3 border-t border-rdc-stone/20">
        <button
          onClick={() => onApprove(request.id)}
          className="btn-ghost text-sm text-green-600 hover:bg-green-50 flex items-center gap-1"
        >
          <Check size={16} />
          Approve
        </button>
        <button
          onClick={() => onReject(request.id)}
          className="btn-ghost text-sm text-red-600 hover:bg-red-50 flex items-center gap-1"
        >
          <X size={16} />
          Reject
        </button>
      </div>
    </div>
  )
}

export default function Providers() {
  const [activeTab, setActiveTab] = useState('providers')
  const [providers, setProviders] = useState([])
  const [claimRequests, setClaimRequests] = useState([])
  const [loading, setLoading] = useState(true)

  // Filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  // Edit modal state
  const [editingProvider, setEditingProvider] = useState(null)
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  // Get unique categories
  const categories = ['all', ...new Set(providers.map(p => p.category).filter(Boolean))]

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)

      // Fetch all service providers
      const { data: providersData, error: providersError } = await supabase
        .from('service_providers')
        .select('*')
        .order('created_at', { ascending: false })

      if (providersError) throw providersError

      // Fetch pending claim requests
      const { data: requestsData, error: requestsError } = await supabase
        .from('provider_claim_requests')
        .select(`
          *,
          provider:service_providers(id, name)
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })

      if (requestsError) throw requestsError

      setProviders(providersData || [])
      setClaimRequests(requestsData || [])
    } catch (err) {
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleVerified = async (providerId, newValue) => {
    try {
      const { error } = await supabase
        .from('service_providers')
        .update({ verified: newValue })
        .eq('id', providerId)

      if (error) throw error

      setProviders(providers.map(p =>
        p.id === providerId ? { ...p, verified: newValue } : p
      ))
    } catch (err) {
      console.error('Error updating verified status:', err)
    }
  }

  const handleToggleFeatured = async (providerId, newValue) => {
    try {
      const { error } = await supabase
        .from('service_providers')
        .update({ featured: newValue })
        .eq('id', providerId)

      if (error) throw error

      setProviders(providers.map(p =>
        p.id === providerId ? { ...p, featured: newValue } : p
      ))
    } catch (err) {
      console.error('Error updating featured status:', err)
    }
  }

  const handleToggleActive = async (providerId, newValue) => {
    try {
      const { error } = await supabase
        .from('service_providers')
        .update({ is_active: newValue })
        .eq('id', providerId)

      if (error) throw error

      setProviders(providers.map(p =>
        p.id === providerId ? { ...p, is_active: newValue } : p
      ))
    } catch (err) {
      console.error('Error updating active status:', err)
    }
  }

  const handleEditOpen = (provider) => {
    setEditingProvider(provider)
    setEditData({
      name: provider.name || '',
      description: provider.description || '',
      location: provider.location || '',
      category: provider.category || '',
      phone: provider.phone || '',
      website: provider.website || '',
      price_range: provider.price_range || '',
      region: provider.region || '',
      verified: provider.verified || false,
      featured: provider.featured || false,
      is_active: provider.is_active || true
    })
  }

  const handleEditChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleEditSave = async () => {
    if (!editingProvider) return

    try {
      setSaving(true)

      const { error } = await supabase
        .from('service_providers')
        .update(editData)
        .eq('id', editingProvider.id)

      if (error) throw error

      // Update local state
      setProviders(providers.map(p =>
        p.id === editingProvider.id ? { ...p, ...editData } : p
      ))

      // Show success message
      setSuccessMessage('Provider updated successfully!')
      setTimeout(() => {
        setSuccessMessage('')
        setEditingProvider(null)
        setEditData({})
      }, 3000)
    } catch (err) {
      console.error('Error updating provider:', err)
      alert('Failed to update provider: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleEditClose = () => {
    setEditingProvider(null)
    setEditData({})
    setSuccessMessage('')
  }

  const handleApproveRequest = async (requestId) => {
    try {
      // Get the claim request details first
      const request = claimRequests.find(r => r.id === requestId)
      if (!request) return

      // Update claim request status
      const { error } = await supabase
        .from('provider_claim_requests')
        .update({
          status: 'approved',
          claim_status: 'approved',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', requestId)

      if (error) throw error

      // If this claim is for an existing provider, update the provider record
      if (request.provider_id) {
        await supabase
          .from('service_providers')
          .update({
            claimed: true,
            claim_status: 'approved',
            claimed_by_email: request.business_email || request.requester_email,
            claimed_at: new Date().toISOString(),
            verified: true
          })
          .eq('id', request.provider_id)
      } else {
        // Create a new service provider from the claim request
        const businessName = request.business_name || 'Unnamed Business'
        const slug = businessName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-' + Date.now()
        await supabase
          .from('service_providers')
          .insert([{
            name: businessName,
            slug,
            category: request.category || 'general',
            location: request.location || '',
            description: request.description || '',
            claimed: true,
            claim_status: 'approved',
            claimed_by_email: request.business_email || request.requester_email,
            claimed_at: new Date().toISOString(),
            verified: true,
            is_active: true
          }])
      }

      // Send approval notification email to the provider
      try {
        await fetch('/api/providers/notify-approval', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: request.business_email || request.requester_email,
            business_name: request.business_name || request.provider?.name,
            requester_name: request.requester_name
          })
        })
      } catch (emailErr) {
        console.error('Failed to send approval email:', emailErr)
        // Don't block the approval if email fails
      }

      setClaimRequests(claimRequests.filter(r => r.id !== requestId))
      // Refresh providers list to show the new/updated provider
      loadData()
    } catch (err) {
      console.error('Error approving request:', err)
    }
  }

  const handleRejectRequest = async (requestId) => {
    try {
      const { error } = await supabase
        .from('provider_claim_requests')
        .update({
          status: 'rejected',
          claim_status: 'rejected',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', requestId)

      if (error) throw error

      setClaimRequests(claimRequests.filter(r => r.id !== requestId))
    } catch (err) {
      console.error('Error rejecting request:', err)
    }
  }

  // Filter providers
  const filteredProviders = providers.filter(p => {
    const matchesSearch =
      !searchTerm ||
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.location && p.location.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter
    const matchesStatus = statusFilter === 'all' || p.claim_status === statusFilter

    return matchesSearch && matchesCategory && matchesStatus
  })

  // Calculate stats
  const stats = {
    totalProviders: providers.length,
    claimedCount: providers.filter(p => p.claim_status === 'approved').length,
    pendingCount: claimRequests.length
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-rdc-forest border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="page-title">Service Providers</h1>
        <p className="text-rdc-taupe mt-1">
          Manage service providers and verify business claims
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rdc-forest/10 flex items-center justify-center">
              <Briefcase size={20} className="text-rdc-forest" />
            </div>
            <div>
              <div className="text-2xl font-bold text-black">{stats.totalProviders}</div>
              <div className="text-xs text-rdc-taupe">Total Providers</div>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Check size={20} className="text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-black">{stats.claimedCount}</div>
              <div className="text-xs text-rdc-taupe">Claimed/Verified</div>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-black">{stats.pendingCount}</div>
              <div className="text-xs text-rdc-taupe">Pending Claims</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-rdc-cream rounded-lg p-1">
        <button
          onClick={() => setActiveTab('providers')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'providers'
              ? 'bg-white text-black shadow-sm'
              : 'text-rdc-taupe hover:text-rdc-charcoal'
          }`}
        >
          <Briefcase size={16} />
          All Providers
          <span className="ml-1 px-1.5 py-0.5 bg-rdc-forest/10 text-rdc-forest text-xs rounded-full">
            {providers.length}
          </span>
        </button>

        <button
          onClick={() => setActiveTab('claims')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'claims'
              ? 'bg-white text-black shadow-sm'
              : 'text-rdc-taupe hover:text-rdc-charcoal'
          }`}
        >
          <Shield size={16} />
          Claim Requests
          {claimRequests.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">
              {claimRequests.length}
            </span>
          )}
        </button>
      </div>

      {/* All Providers Tab */}
      {activeTab === 'providers' && (
        <>
          {/* Search and Filters */}
          <div className="mb-6 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-rdc-taupe" />
              <input
                type="text"
                placeholder="Search by name or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 w-full"
              />
            </div>

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {/* Category Filter */}
              <div className="relative inline-block">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="btn-secondary appearance-none pr-8 flex items-center gap-2"
                >
                  <option value="all">All Categories</option>
                  {categories.slice(1).map(cat => (
                    <option key={cat} value={cat}>
                      {cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : 'Uncategorized'}
                    </option>
                  ))}
                </select>
                <ChevronDown size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-rdc-charcoal" />
              </div>

              {/* Status Filter */}
              <div className="relative inline-block">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="btn-secondary appearance-none pr-8 flex items-center gap-2"
                >
                  <option value="all">All Status</option>
                  <option value="unclaimed">Unclaimed</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <ChevronDown size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-rdc-charcoal" />
              </div>
            </div>
          </div>

          {/* Providers List */}
          {filteredProviders.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-rdc-cream flex items-center justify-center">
                <Briefcase size={28} className="text-rdc-taupe" />
              </div>
              <h3 className="section-title mb-2">No providers found</h3>
              <p className="text-body text-rdc-charcoal">
                {searchTerm || categoryFilter !== 'all' || statusFilter !== 'all'
                  ? 'Try adjusting your filters or search term'
                  : 'No service providers yet'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProviders.map(provider => (
                <ProviderRow
                  key={provider.id}
                  provider={provider}
                  onToggleVerified={handleToggleVerified}
                  onToggleFeatured={handleToggleFeatured}
                  onToggleActive={handleToggleActive}
                  onEdit={handleEditOpen}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Edit Modal */}
      {editingProvider && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-black">Edit Provider</h2>
              <button
                onClick={handleEditClose}
                className="text-rdc-taupe hover:text-black transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {successMessage && (
              <div className="mb-4 p-3 bg-green-100 border border-green-300 rounded-lg text-green-700 text-sm">
                {successMessage}
              </div>
            )}

            <div className="space-y-4 mb-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Name</label>
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => handleEditChange('name', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Description</label>
                <textarea
                  value={editData.description}
                  onChange={(e) => handleEditChange('description', e.target.value)}
                  className="input-field w-full min-h-[100px]"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Location</label>
                <input
                  type="text"
                  value={editData.location}
                  onChange={(e) => handleEditChange('location', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Category</label>
                <select
                  value={editData.category}
                  onChange={(e) => handleEditChange('category', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Select a category</option>
                  <option value="mechanic">Mechanic</option>
                  <option value="body_shop">Body Shop</option>
                  <option value="detailing">Detailing</option>
                  <option value="upholstery">Upholstery</option>
                  <option value="electrical">Electrical</option>
                  <option value="tires">Tires</option>
                  <option value="performance">Performance</option>
                  <option value="restoration">Restoration</option>
                  <option value="storage">Storage</option>
                  <option value="transport">Transport</option>
                  <option value="general">General</option>
                </select>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Phone</label>
                <input
                  type="tel"
                  value={editData.phone}
                  onChange={(e) => handleEditChange('phone', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Website</label>
                <input
                  type="url"
                  value={editData.website}
                  onChange={(e) => handleEditChange('website', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Price Range</label>
                <select
                  value={editData.price_range}
                  onChange={(e) => handleEditChange('price_range', e.target.value)}
                  className="input-field w-full"
                >
                  <option value="">Select price range</option>
                  <option value="$">$ - Budget</option>
                  <option value="$$">$$ - Moderate</option>
                  <option value="$$$">$$$ - Premium</option>
                  <option value="$$$$">$$$$ - Luxury</option>
                </select>
              </div>

              {/* Region */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">Region</label>
                <input
                  type="text"
                  value={editData.region}
                  onChange={(e) => handleEditChange('region', e.target.value)}
                  className="input-field w-full"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-3 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.verified}
                    onChange={(e) => handleEditChange('verified', e.target.checked)}
                    className="w-4 h-4 rounded border-rdc-stone text-rdc-primary"
                  />
                  <span className="text-sm font-medium text-black">Verified</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.featured}
                    onChange={(e) => handleEditChange('featured', e.target.checked)}
                    className="w-4 h-4 rounded border-rdc-stone text-rdc-primary"
                  />
                  <span className="text-sm font-medium text-black">Featured</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.is_active}
                    onChange={(e) => handleEditChange('is_active', e.target.checked)}
                    className="w-4 h-4 rounded border-rdc-stone text-rdc-primary"
                  />
                  <span className="text-sm font-medium text-black">Active</span>
                </label>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-3 pt-4 border-t border-rdc-stone/20">
              <button
                onClick={handleEditClose}
                className="btn-secondary flex-1"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                className="btn-primary flex-1"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim Requests Tab */}
      {activeTab === 'claims' && (
        <>
          {claimRequests.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <Check size={28} className="text-green-600" />
              </div>
              <h3 className="section-title mb-2">All caught up!</h3>
              <p className="text-body text-rdc-charcoal">
                No pending claim requests to review
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {claimRequests.map(request => (
                <ClaimRequestRow
                  key={request.id}
                  request={request}
                  onApprove={handleApproveRequest}
                  onReject={handleRejectRequest}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
